import { z } from "zod";
import { apiClient } from "./api-client";
import { getEnv } from "./env";
import { useTenantOverride } from "./tenant-override";

// Catalog admin API + shared product schema. Create and Edit reuse the same field
// component (ProductFields) and this schema so there is a single source of truth.
// Contracts: POST/PUT /admin/api/v1/catalog/products, GET /api/v1/catalog/products/{slug}.

export type ProductLine = "SHAKTI" | "SATTVA" | "AMRITA" | "AGNI" | "TRIAL";

export interface ProductView {
  id: number;
  slug?: string;
  name?: string;
  line?: ProductLine | string;
  status?: string;
  shortDescription?: string;
  longDescription?: string;
  classicalReference?: string;
  subscribable?: boolean;
  trialPack?: boolean;
  displayOrder?: number;
  variants?: Array<{ id: number; sku?: string; name?: string }>;
}

// Fields common to create + edit (the editable product body).
export const productFieldsSchema = z.object({
  name: z.string().min(2, "At least 2 characters").max(200),
  shortDescription: z.string().min(10, "At least 10 characters"),
  longDescription: z.string().optional(),
  classicalReference: z.string().optional(),
  subscribable: z.boolean(),
});
export type ProductFieldsValues = z.infer<typeof productFieldsSchema>;

// Create adds slug/line/trialPack (immutable after create — not in UpdateProductInput).
export const createProductSchema = productFieldsSchema.extend({
  slug: z
    .string()
    .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, "kebab-case only (a-z, 0-9, hyphens)")
    .max(100),
  line: z.enum(["SHAKTI", "SATTVA", "AMRITA", "AGNI", "TRIAL"]),
  trialPack: z.boolean(),
});
export type CreateProductValues = z.infer<typeof createProductSchema>;

// Edit adds displayOrder (per UpdateProductInput); slug/line/trialPack are read-only.
export const editProductSchema = productFieldsSchema.extend({
  displayOrder: z.coerce.number().int().min(0).optional(),
});
export type EditProductValues = z.infer<typeof editProductSchema>;

export type ProductStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export interface ProductSummary {
  id: number;
  slug?: string;
  name?: string;
  line?: ProductLine | string;
  status?: ProductStatus | string;
  madeForIndia?: boolean;
  fromPricePaise?: number;
}

/** Admin product list (BLK-API-002): includes DRAFT/ARCHIVED; server-side search. */
export function listProductsForAdmin(params: {
  status?: ProductStatus | "";
  search?: string;
  page?: number;
  size?: number;
}): Promise<ProductSummary[]> {
  const q = new URLSearchParams();
  if (params.status) q.set("status", params.status);
  // "%" = match-all: omitting `search` 500s server-side (null param bound as bytea
  // in the JPQL lower(...) — BLK-API-020), so always send a value.
  q.set("search", params.search || "%");
  q.set("page", String(params.page ?? 0));
  q.set("size", String(params.size ?? 50));
  return apiClient.request<ProductSummary[]>(`/admin/api/v1/catalog/products?${q.toString()}`);
}

export async function getProductBySlug(slug: string): Promise<ProductView> {
  // Public endpoint fetched WITHOUT the bearer token: an authenticated platform
  // admin resolves to the __PLATFORM__ tenant on public reads and 404s, while an
  // anonymous request resolves from X-Tenant-Code (the switched tenant's code).
  // Residual BLK-API-002 gap: there is no admin product-detail read yet.
  const tenantCode =
    useTenantOverride.getState().tenantCode ?? getEnv().VITE_DEFAULT_TENANT_CODE ?? "nutrayan";
  const res = await fetch(`/api/v1/catalog/products/${encodeURIComponent(slug)}`, {
    headers: { Accept: "application/json", "X-Tenant-Code": tenantCode },
  });
  if (!res.ok) throw new Error(`Couldn't load product "${slug}" (HTTP ${res.status})`);
  const payload = (await res.json()) as { data?: ProductView };
  return payload.data ?? (payload as unknown as ProductView);
}

export function createProduct(input: CreateProductValues): Promise<ProductView> {
  return apiClient.request<ProductView>("/admin/api/v1/catalog/products", {
    method: "POST",
    body: input,
  });
}

export function updateProduct(id: number, input: EditProductValues): Promise<ProductView> {
  // Maps to UpdateProductInput {name, shortDescription, longDescription,
  // classicalReference, subscribable, displayOrder}.
  return apiClient.request<ProductView>(`/admin/api/v1/catalog/products/${id}`, {
    method: "PUT",
    body: input,
  });
}
